import { DeleteOutline, PublicOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  QButton,
  QCheckbox,
  QChip,
  QIconButton,
  QTable,
  QTableBody,
  QTableCell,
  QTableHead,
  QTableRow,
  QTableSortableColumnHeader,
  QTooltip,
} from '@kw/quokka-ui';
import { CountryFlag } from '../../CountryFlag';
import { OrganizationSettingPanel } from './OrganizationSettingCard';
import { normalizeCountryName } from './countrySettings';
import { formatRiskyNationDate } from './RiskyNations.utils';
import { HighRiskCountry, SortDirection, SortField } from './RiskyNations.types';

interface RiskyNationsPanelProps {
  countries: HighRiskCountry[];
  total: number;
  ofacCount: number;
  customCount: number;
  latestUpdatedDate: string | null;
  isAdmin: boolean;
  isLoading: boolean;
  hasError: boolean;
  isSaving: boolean;
  canAddCountry: boolean;
  restoreDefaultsDisabled: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onAddCountry: () => void;
  onClear: () => void;
  onRestore: () => void;
  onRemove: (country: HighRiskCountry) => void;
  selectedCountryCodes: string[];
  onToggleCountrySelection: (countryCode: string) => void;
  onToggleAllCountrySelection: (selected: boolean) => void;
  onRemoveSelected: () => void;
}

type RiskyNationsTableProps = Pick<
  RiskyNationsPanelProps,
  | 'countries'
  | 'hasError'
  | 'isAdmin'
  | 'isLoading'
  | 'isSaving'
  | 'onRemove'
  | 'onSort'
  | 'onToggleAllCountrySelection'
  | 'onToggleCountrySelection'
  | 'selectedCountryCodes'
  | 'sortDirection'
  | 'sortField'
>;

const DESCRIPTION =
  "Used to determine which Server Name Indicator (SNI) geolocations discovered during network analysis of an app will be flagged as risky. The defaults are countries sanctioned by the US Department of the Treasury's Office of Foreign Assets Control (OFAC). Edit this list to align to your organization's risk posture.";

const RiskyNationsTable = ({
  countries,
  hasError,
  isAdmin,
  isLoading,
  isSaving,
  onRemove,
  onSort,
  onToggleAllCountrySelection,
  onToggleCountrySelection,
  selectedCountryCodes,
  sortDirection,
  sortField,
}: RiskyNationsTableProps) => {
  if (hasError) {
    return <StatusMessage role="alert">Risky Nations could not be loaded. Refresh the page to try again.</StatusMessage>;
  }

  if (isLoading) return <StatusMessage>Loading risky nations…</StatusMessage>;

  if (!countries.length) {
    return <EmptyState>No risky nations configured — add a country or restore Q-scout defaults.</EmptyState>;
  }

  const selectedCountryCodeSet = new Set(selectedCountryCodes);
  const selectedCount = countries.filter(country => selectedCountryCodeSet.has(country.countryCode)).length;
  const allCountriesSelected = selectedCount === countries.length;

  return (
    <QTable aria-label="Risky Nations">
      <QTableHead>
        <QTableRow>
          {isAdmin && (
            <SelectionCell padding="checkbox">
              <QCheckbox
                checked={allCountriesSelected}
                indeterminate={selectedCount > 0 && !allCountriesSelected}
                disabled={isSaving}
                onChange={(_event, checked) => onToggleAllCountrySelection(checked)}
                inputProps={{ 'aria-label': 'Select all risky nations' }}
              />
            </SelectionCell>
          )}
          <QTableSortableColumnHeader
            title="Country"
            sortValue={sortField === 'country' ? sortDirection : null}
            onClick={() => onSort('country')}
          />
          <QTableSortableColumnHeader
            title="Date Added"
            sortValue={sortField === 'dateAdded' ? sortDirection : null}
            onClick={() => onSort('dateAdded')}
          />
          {isAdmin && <ActionsHeader>Actions</ActionsHeader>}
        </QTableRow>
      </QTableHead>
      <QTableBody>
        {countries.map(country => {
          const isSelected = selectedCountryCodeSet.has(country.countryCode);
          const removesSelection = isSelected && selectedCount > 1;
          const removeLabel = removesSelection
            ? `Remove ${selectedCount} selected countries`
            : `Remove ${normalizeCountryName(country.name)}`;

          return (
            <QTableRow key={country.countryCode} selected={isSelected}>
              {isAdmin && (
                <SelectionCell padding="checkbox">
                  <QCheckbox
                    checked={isSelected}
                    disabled={isSaving}
                    onChange={() => onToggleCountrySelection(country.countryCode)}
                    inputProps={{
                      'aria-label': `Select ${normalizeCountryName(country.name)}`,
                      'aria-checked': isSelected,
                    }}
                  />
                </SelectionCell>
              )}
              <QTableCell>
                <CountryCell>
                  <CountryFlag countryCode={country.countryCode} />
                  <CountryName>{normalizeCountryName(country.name)}</CountryName>
                  <SourceBadge data-source={country.source}>{country.source}</SourceBadge>
                </CountryCell>
              </QTableCell>
              <QTableCell>{formatRiskyNationDate(country.dateAdded) || '—'}</QTableCell>
              {isAdmin && (
                <ActionCell>
                  <QTooltip title={removeLabel}>
                    <TrashButton aria-label={removeLabel} disabled={isSaving} onClick={() => onRemove(country)}>
                      <DeleteOutline fontSize="small" />
                    </TrashButton>
                  </QTooltip>
                </ActionCell>
              )}
            </QTableRow>
          );
        })}
      </QTableBody>
    </QTable>
  );
};

export const RiskyNationsPanel = ({
  countries,
  total,
  ofacCount,
  customCount,
  latestUpdatedDate,
  isAdmin,
  isLoading,
  hasError,
  isSaving,
  canAddCountry,
  restoreDefaultsDisabled,
  sortField,
  sortDirection,
  onSort,
  onAddCountry,
  onClear,
  onRestore,
  onRemove,
  selectedCountryCodes,
  onToggleCountrySelection,
  onToggleAllCountrySelection,
  onRemoveSelected,
}: RiskyNationsPanelProps) => (
  <OrganizationSettingPanel
    title="Risky Nations"
    description={DESCRIPTION}
    icon={
      <RiskyNationsIcon>
        <PublicOutlined />
        <RiskIndicator>!</RiskIndicator>
      </RiskyNationsIcon>
    }
    titleAdornment={<ConfiguredChip label={`${total} configured`} size="small" />}
    actions={
      isAdmin ? (
        <>
          <QButton variant="ghost" disabled={isLoading || hasError || !total || isSaving} onClick={onClear}>
            Clear list
          </QButton>
          <QButton variant="ghost" disabled={isLoading || hasError || restoreDefaultsDisabled || isSaving} onClick={onRestore}>
            Restore defaults
          </QButton>
          <AddCountryButton variant="filled" disabled={!canAddCountry || isSaving} onClick={onAddCountry}>
            Add Country
          </AddCountryButton>
        </>
      ) : undefined
    }
    fullWidthContent={
      <>
        <SummaryStrip>
          <SummaryItem>
            <SummaryLabel>Configured</SummaryLabel>
            <SummaryValue>{total}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>OFAC defaults</SummaryLabel>
            <SummaryValue>{ofacCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Custom additions</SummaryLabel>
            <SummaryValue>{customCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Last updated</SummaryLabel>
            <SummaryDateValue>{latestUpdatedDate || '—'}</SummaryDateValue>
          </SummaryItem>
        </SummaryStrip>

        {isAdmin && !isLoading && !hasError && selectedCountryCodes.length > 0 && (
          <BulkSelectionBar>
            <SelectedCount>
              {selectedCountryCodes.length} {selectedCountryCodes.length === 1 ? 'country' : 'countries'} selected
            </SelectedCount>
            <QButton variant="ghost" disabled={isSaving} onClick={onRemoveSelected}>
              Remove selected
            </QButton>
          </BulkSelectionBar>
        )}

        <TableContainer>
          <RiskyNationsTable
            countries={countries}
            hasError={hasError}
            isAdmin={isAdmin}
            isLoading={isLoading}
            isSaving={isSaving}
            onRemove={onRemove}
            onSort={onSort}
            selectedCountryCodes={selectedCountryCodes}
            onToggleCountrySelection={onToggleCountrySelection}
            onToggleAllCountrySelection={onToggleAllCountrySelection}
            sortDirection={sortDirection}
            sortField={sortField}
          />
        </TableContainer>
      </>
    }
  />
);

const RiskyNationsIcon = styled('span')`
  position: relative;
  display: flex;
  width: 21px;
  height: 21px;
`;

const RiskIndicator = styled('span')`
  position: absolute;
  right: -2px;
  bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 11px;
  height: 11px;
  border: 1px solid #f3f5f5;
  border-radius: 50%;
  background: #e69622;
  color: #ffffff;
  font-size: 8px;
  line-height: 1;
  font-weight: 800;
`;

const ConfiguredChip = styled(QChip)`
  height: 24px;
  border-radius: 12px;
  background: #e8f3f3;
  color: #38575f;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
`;

const AddCountryButton = styled(QButton)`
  min-width: 116px;
`;

const SummaryStrip = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 20px 24px 22px;
  overflow: hidden;
  border: 1px solid #dce3e5;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: 620px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const SummaryItem = styled('div')`
  min-width: 0;
  padding: 14px 16px;
  border-right: 1px solid #dce3e5;

  &:last-child {
    border-right: 0;
  }
`;

const SummaryLabel = styled('div')`
  color: #65767c;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const SummaryValue = styled('div')`
  margin-top: 5px;
  color: #1d3138;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
`;

const SummaryDateValue = styled('div')`
  margin-top: 7px;
  color: #1d3138;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`;

const TableContainer = styled('div')`
  border-top: 1px solid #dce3e5;
  overflow-x: auto;

  table {
    min-width: 560px;
  }

  th {
    color: #66777d;
    font-size: 12px;
    line-height: 17px;
  }

  td {
    height: 52px;
    color: #354a51;
    font-size: 13px;
    line-height: 18px;
  }

  tr {
    background: #ffffff;
  }
`;

const BulkSelectionBar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 4px 24px;
  border-top: 1px solid #dce3e5;
  background: #f7fafa;
`;

const SelectedCount = styled('span')`
  color: #38575f;
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
`;

const SelectionCell = styled(QTableCell)`
  box-sizing: border-box;
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 0 0 0 4px !important;
`;

const ActionsHeader = styled(QTableCell)`
  width: 80px;
  color: #66777d;
  font-size: 12px;
  line-height: 17px;
  font-weight: 800;
  text-align: right;
`;

const CountryCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const CountryName = styled('span')`
  min-width: 0;
  color: #1d3138;
  font-weight: 600;
`;

const SourceBadge = styled('span')`
  padding: 1px 6px;
  border: 1px solid #dce3e5;
  border-radius: 5px;
  color: #64777d;
  font-size: 9px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;

  &[data-source='CUSTOM'] {
    border-color: transparent;
    background: #e4f1f2;
    color: #2f626b;
  }
`;

const ActionCell = styled(QTableCell)`
  text-align: right;
`;

const TrashButton = styled(QIconButton)`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  color: #63777d;

  &:hover {
    background: #f1f4f5;
  }
`;

const StatusMessage = styled('div')`
  padding: 36px 24px;
  color: #66777d;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
`;

const EmptyState = styled(StatusMessage)`
  padding: 48px 24px;
`;
